import React from 'react';
import { Link } from 'react-router-dom';

import { IconInfoCircle, IconX, IconLeftArrow } from '@trqk/components';
import { dateToShortDate, locale, updateURLSearchParams } from '@trqk/utils';

import { FULL_PATHS, URL_PARAMS } from '../../../../../constants';

interface ManageGapAnalysisHeaderProps {
    /**
     * See {@link ManageGapAnalysisHeaderData}
     */
    data: ManageGapAnalysisHeaderData;
    /**
     * The `remove` function provided to the Manage Statements modal
     */
    remove(removeModalInput: RemoveModalInput): void;
    /**
     * SetState function that will allow the user to permanently dismiss this message
     */
    setDidCloseHeader: SetState<boolean>;
}

const messages = locale.enUS.MANAGE_STATEMENTS.MANAGE_GAP_ANALYSIS_HEADER;

/**
 * When the user has selected a performance period within the Create Gap Analysis modal that is
 * missing a statement, this header is shown to prompt them to upload that specific statement.
 * @param props - {@link ManageGapAnalysisHeaderProps}
 * @returns JSX
 */
export const ManageGapAnalysisHeader = ({
    data,
    remove,
    setDidCloseHeader,
}: ManageGapAnalysisHeaderProps): JSX.Element => {
    const { statementPeriodLabel, performancePeriod, pro } = data;

    let performancePeriodMessage = messages.YOU_SELECTED;

    if (performancePeriod.start !== null && performancePeriod.end !== null) {
        performancePeriodMessage = `${messages.YOU_SELECTED}, ${
            messages.WHICH_RUNS
        } ${messages.FROM} ${dateToShortDate(performancePeriod.start)} ${
            messages.TO
        } ${dateToShortDate(performancePeriod.end)}`;
    }

    const statementLabel = pro
        ? `${pro} ${statementPeriodLabel}`
        : statementPeriodLabel;

    return (
        <div className="flex border-dotted border-2 border-primary-500 pt-4 px-4 pb-3 m-4">
            <div
                className="grid items-start"
                style={{ gridTemplateColumns: '24px auto' }}
            >
                <IconInfoCircle
                    addClasses="mt-1"
                    fillColor="primary-500"
                    width={16}
                />
                <div className="text-sm">
                    <p className="text-tertiary-400">
                        {messages.NEED_TO_UPLOAD}{' '}
                        <span className="font-semibold">{statementLabel}</span>{' '}
                        {`${messages.STATEMENT_TO} ${performancePeriodMessage}`}
                        .
                    </p>
                    <div className="flex justify-end">
                        <Link
                            className="text-primary-500 underline respond-to-mouse"
                            to={({ search }) => ({
                                search: `?${updateURLSearchParams(search, [
                                    {
                                        key: URL_PARAMS.CONTINUE_MANAGE_GAP_ANALYSIS,
                                        value: true,
                                    },
                                ])}`,
                                pathname: FULL_PATHS.MANAGE_GAP_ANALYSIS,
                                state: {
                                    noAnchorScroll: true,
                                },
                            })}
                            onClick={() => {
                                remove({
                                    success: true,
                                    canceled: false,
                                });
                            }}
                        >
                            <IconLeftArrow
                                addClasses="mr-1"
                                fillColor="tertiary-400"
                            />
                            {messages.CONTINUE_GAP_ANALYSIS_SETUP}
                        </Link>
                    </div>
                </div>
            </div>
            <IconX
                addClasses="flex-none respond-to-mouse"
                fillColor="tertiary-400"
                handleClick={() => {
                    setDidCloseHeader(true);
                }}
                width={12}
            />
        </div>
    );
};
